import React, { useEffect, useState } from "react";
import {
  Table,
  Input,
  message,
  Button,
  Space,
  Spin,
  Typography,
  notification,
  Modal,
  Card,
  Tooltip,
  Alert,
  Select,
  Row,
  Col,
  Statistic,
} from "antd";
import {
  SearchOutlined,
  FilePdfOutlined,
  ClearOutlined,
  PlusOutlined,
  EyeOutlined,
  EditOutlined,
} from "@ant-design/icons";
import getNovedades from "../../services/novedades/getNovedades";
import FormularioNovedades from "./FormNovedades";
import PdfNovedad from "./PdfNovedad";
import getTipoNovedades from "../../services/novedades/getTipoNovedadService";
import { useSelector } from "react-redux";
import { selectAppPermissions } from "../../store/auth/selectors";
import getNovedadById from "../../services/novedades/getNovedadByIdService";
import ExportNovedades from "./NovedadesExcel";
import config from "../../config";

const baseUrl = config.baseUrl;
const { Title } = Typography;
const { Option } = Select;

const TablaNovedades = () => {
  const [novedades, setNovedades] = useState([]);
  const [listatipoNovedades, setListaTipoNovedades] = useState([]);
  const [selectedTipoNovedad, setSelectedTipoNovedad] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [showForm, setShowForm] = useState(false); // Estado para controlar la visibilidad del formulario
  const [showFormModal, setShowFormModal] = useState(false); // Estado para controlar la visibilidad del modal
  const [initialData, setInitialData] = useState({}); // Estado para manejar los datos iniciales
  const [showPdfNovedades, setShowPdfNovedades] = useState(false); // Estado para mostrar el componente pdfNovedades
  const [isEditMode, setIsEditMode] = useState(false); // Estado para controlar el modo de edición

  const [departamentos, setDepartamentos] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [selectedDepartamento, setSelectedDepartamento] = useState(null);
  const [selectedMunicipio, setSelectedMunicipio] = useState(null);

  const appName = "novedades";
  const permissions = useSelector((state) =>
    selectAppPermissions(state, appName)
  );
  const permissionInsert = permissions.priv_insert === "Y";
  const permissionPrint = permissions.priv_print === "Y";
  const permissionUpdate = permissions.priv_update === "Y";
  const permissionExport = permissions.priv_export === "Y";



  const usernameFromState = useSelector((state) => state.auth.user);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const handleCancel = () => {
    if (initialData && Object.keys(initialData).length > 0) {
      Modal.confirm({
        title: "¿Está segur@ de que desea cerrar?",
        content:
          "Si cierra esta ventana, la información ingresada no se guardará.",
        okText: "Sí, cerrar",
        cancelText: "No",
        onOk: () => {
          setShowFormModal(false);
          setInitialData({});
          setIsEditMode(false);
        },
      });
    } else {
      Modal.confirm({
        title: "¿Está segur@ de que desea cerrar?",
        content:
          "Si cierra esta ventana, la información ingresada no se guardará.",
        okText: "Sí, cerrar",
        cancelText: "No",
        onOk: () => {
          setShowFormModal(false);
          setInitialData({});
          setIsEditMode(false);
        },
      });
    }
  };

  const fetchNovedades = async () => {
    try {
      const response = await getNovedades(usernameFromState.email);
      const { status, data } = response;

      if (status === 200) {
        setNovedades(data);
        setFilteredData(data || []);
      } else {
        notification.error({
          message: "Error",
          description: message || "Failed to fetch novedades data.",
        });
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "An error occurred while fetching data.",
      });
    } finally {
      setLoading(false);
    }
  };

  const obtenerTipoNovedades = async () => {
    try {
      setListaTipoNovedades([]);
      const { data } = await getTipoNovedades();
      setListaTipoNovedades(data);
    } catch (error) {
      console.error("Error al obtener tipo de novedades:", error);
    }
  };

  const handletTipoNovedad = (value) => {
    setSelectedTipoNovedad(value);
    filterData(selectedDepartamento, selectedMunicipio, value, searchText);
  };
  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchText(value);
    filterData(
      selectedDepartamento,
      selectedMunicipio,
      selectedTipoNovedad,
      value
    );
  };

  const filterData = (departamento, municipio, tipoNovedad, texto) => {
    let filtered = [...novedades];

    // Filtro por departamento (si está seleccionado en el select)
    if (departamento) {
      filtered = filtered.filter((n) => n.departamento_p === departamento);
    }
    // Filtro por municipio (si está seleccionado en el select)
    if (municipio) {
      filtered = filtered.filter((n) => n.municipio_p === municipio);
    }
    // Filtro por tipo de novedad (si está seleccionado en el select)
    if (tipoNovedad) {
      filtered = filtered.filter((n) => n.tipoNovedad === tipoNovedad);
    }

    // Filtro por texto de búsqueda
    if (texto?.trim()) {
      // Verifica que no sea nulo, vacío o solo espacios
      filtered = filtered.filter((novedad) =>
        Object.values(novedad).some(
          (val) =>
            typeof val === "string" &&
            val.toLowerCase().includes(texto.toLowerCase())
        )
      );
    }

    // Actualiza los datos filtrados
    setFilteredData(filtered);
  };

  const handleClearFilters = () => {
    setSelectedTipoNovedad(null);
    setSelectedDepartamento(null);
    setSelectedMunicipio(null);
    setSearchText("");
    setFilteredData(novedades);
  };

  useEffect(() => {
    obtenerTipoNovedades();
    fetchNovedades();
  }, []);

  useEffect(() => {
    // Obtener lista única de departamentos de los participants
    const uniqueDepartamentos = [
      ...new Set(novedades.map((n) => n.departamento_p)),
    ].sort();
    setDepartamentos(uniqueDepartamentos);
    setSelectedDepartamento(null);
  }, [novedades]);

  const fetchNovedadById_view = async (id) => {
    try {
      const response = await getNovedadById(id);
      const { status, data } = response;

      if (status) {
        setInitialData(data);
        setIsEditMode(null); // Modo visualización
        setShowFormModal(true); // Abre el modal
      } else {
        notification.error({
          message: response.message,
        });
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "An error occurred while fetching data.",
      });
    }
  };

  // Nueva función para edición
  const fetchNovedadById_edit = async (id) => {
    try {
      const response = await getNovedadById(id);
      const { status, data } = response;

      if (status) {
        setInitialData(data);
        setIsEditMode(true); // Modo edición
        setShowFormModal(true); // Abre el modal
      } else {
        notification.error({
          message: response.message,
        });
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "An error occurred while fetching data.",
      });
    }
  };

  const fetchNovedadById_pdf = async (id) => {

    try {
      const response = await getNovedadById(id);
      const { status, data } = response;

      if (status) {
        if (data?.ruta_pdf != null) {
          // 👉 Generar URL con timestamp para evitar caché
          const urlConTimestamp = `${baseUrl}${
            data.ruta_pdf
          }?t=${new Date().getTime()}`;

          try {
            // 👉 Validar si el recurso existe (HEAD es más liviano que GET)
            const headResponse = await fetch(urlConTimestamp, {
              method: "HEAD",
            });

            if (headResponse.ok) {
              // ✅ El PDF existe → abrir en nueva ventana
              window.open(urlConTimestamp, "_blank");
            } else {
              // ❌ El archivo no existe (404, 500, etc.) → abrir modal
              console.error(
                `❌ PDF roto (HTTP ${headResponse.status}) → ID: ${id} | URL: ${urlConTimestamp}`
              );
              setInitialData(data);
              setShowPdfNovedades(true);
            }
          } catch (fetchError) {
            console.error(
              `⚠️ Error al verificar PDF (ID: ${id}) → ${urlConTimestamp}`,
              fetchError
            );
            // 👉 Mostrar modal en caso de error de conexión
            setInitialData(data);
            setShowPdfNovedades(true);
          }
        } else {

          console.log("esta es la data")
          console.log(data)
          // 👉 Si no hay ruta_pdf definida → abrir modal
          setInitialData(data);
          setShowPdfNovedades(true);
        }
      } else {
        notification.error({
          message: response.message,
        });
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "An error occurred while fetching data.",
      });
    }
  };

  const handleDepartamentoChange = (value) => {
    setSelectedDepartamento(value);
    setSelectedMunicipio(null); // Resetear municipio cuando cambia departamento

    // Obtener municipios del departamento seleccionado
    const municipiosFiltrados = [
      ...new Set(
        novedades
          .filter((p) => p.departamento_p === value)
          .map((p) => p.municipio_p)
      ),
    ].sort();
    setMunicipios(municipiosFiltrados);

    // Filtrar datos
    filterData(value, null, selectedTipoNovedad, searchText);
  };

  const handleMunicipioChange = (value) => {
    setSelectedMunicipio(value);
    filterData(selectedDepartamento, value, selectedTipoNovedad, searchText);
  };

  const columns = [
    {
      title: "Acciones",
      key: "acciones",
      width: 100,
      render: (text, record) => (
        <Space>
     <Tooltip title="Visualizar Novedad">
              <Button
                style={{ backgroundColor: "#FFA500", color: "#fff" }}
                icon={<EyeOutlined />}
                onClick={() => {
                  fetchNovedadById_view(record.id);
                }}
              ></Button>
            </Tooltip>
        
       
        
     {permissionUpdate && (
            <Tooltip title="Editar Novedad">
              <Button
                style={{ backgroundColor: "#52c41a", color: "#fff" }}
                icon={<EditOutlined />}
                onClick={() => {
                  fetchNovedadById_edit(record.id);
                }}
              ></Button>
            </Tooltip>
     )}
        {permissionPrint && (
            <Tooltip title="Descargar PDF">
              <Button
                type="default"
                icon={<FilePdfOutlined />}
                style={{ backgroundColor: "#1890ff", color: "#fff" }}
                onClick={() => {
                  fetchNovedadById_pdf(record.id);
                }}
              ></Button>
            </Tooltip>
        )}


        
        </Space>
      ),
    },
       {
      title: "id",
      dataIndex: "id",
      key: "id",
      width: 100,
    },

    {
      title: "Tipo de novedad",
      dataIndex: "novedad",
      key: "novedad",
      width: 250,
    },
    {
      title: "Fecha Registro",
      dataIndex: "fecha_registro",
      key: "fecha_registro",
      width: 150,
    },
    {
      title: "Codigo SIR",
      dataIndex: "codigoSIRR",
      key: "codigoSIRR",
      width: 150,
      fixed: "left",
      ellipsis: true,
    },

    {
      title: "Número Identificación",
      dataIndex: "numeroIdentificacion",
      key: "numeroIdentificacion",
      width: 200,
    },
    {
      title: "Nombre Participante",
      dataIndex: "nombreParticipante",
      key: "nombreParticipante",
      width: 200,
    },
    {
      title: "Código PREPP",
      dataIndex: "codigoPREPP",
      key: "codigoPREPP",
      width: 150,
    },

    {
      title: "Descripción Proyecto",
      dataIndex: "descripcionProyecto",
      key: "descripcionProyecto",
      width: 300,
      render: (text) => (
        <Tooltip title={text}>
          <span>{text.length > 50 ? `${text.substring(0, 50)}...` : text}</span>
        </Tooltip>
      ),
    },
    {
      title: "Nombre Proyecto",
      dataIndex: "nombreProyecto",
      key: "nombreProyecto",
      width: 250,
    },

    {
      title: "Departamento",
      dataIndex: "departamento",
      key: "departamento",
      width: 150,
    },
    {
      title: "Municipio",
      dataIndex: "municipio",
      key: "municipio",
      width: 150,
    },
    {
      title: "Vereda",
      dataIndex: "vereda",
      key: "vereda",
      width: 150,
    },
    {
      title: "Dirección",
      dataIndex: "direccion",
      key: "direccion",
      width: 200,
    },

    {
      title: "Sector Economico",
      dataIndex: "sectorEconomico",
      key: "sectorEconomico",
      width: 250,
    },
    {
      title: "Actividad Productiva",
      dataIndex: "actividadProductiva",
      key: "actividadProductiva",
      width: 250,
    },
    {
      title: "Observaciones",
      dataIndex: "observaciones",
      key: "observaciones",
      width: 300,
    },
    {
      title: "Titular",
      dataIndex: "nombreTitular",
      key: "nombreTitular",
      width: 200,
    },
    {
      title: "C.C Titular",
      dataIndex: "ccTitular",
      key: "ccTitular",
      width: 150,
    },
    {
      title: "Profesional FAO",
      dataIndex: "nombreContratista",
      key: "nombreContratista",
      width: 200,
    },
    {
      title: "C.C Profesional FAO",
      dataIndex: "ccContratista",
      key: "ccContratista",
      width: 150,
    },
  ];

  return (
    <>
      {showPdfNovedades ? (
        <PdfNovedad
          formData={initialData}
          onClose={() => setShowPdfNovedades(false)}
        />
      ) : (
        <>
          {loading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100vh",
              }}
            >
              <Spin size="large" />
            </div>
          ) : (
            <>
            <Row gutter={[16, 16]} className="mb-4">
                <Col span={24}>
                  <div className="filter-container">
                    <Row gutter={[16, 16]} align="middle">
                      {/* Filtro Departamento */}
                      <Col xs={24} sm={12} md={6} lg={4}>
                        <div className="filter-item">
                          <Title
                            level={5}
                            className="filter-label"
                            style={{ color: "#042956" }}
                          >
                            Departamento
                          </Title>
                          <Select
                            style={{ width: "100%" }}
                            placeholder="Seleccione departamento"
                            allowClear
                            onChange={handleDepartamentoChange}
                            value={selectedDepartamento}
                          >
                            {departamentos.map((depto) => (
                              <Select.Option key={depto} value={depto}>
                                {depto}
                              </Select.Option>
                            ))}
                          </Select>
                        </div>
                      </Col>

                      {/* Filtro Municipio */}
                      <Col xs={24} sm={12} md={6} lg={4}>
                        <div className="filter-item">
                          <Title
                            level={5}
                            className="filter-label"
                            style={{ color: "#042956" }}
                          >
                            Municipio
                          </Title>
                          <Select
                            style={{ width: "100%" }}
                            placeholder="Seleccione municipio"
                            allowClear
                            disabled={!selectedDepartamento}
                            onChange={handleMunicipioChange}
                            value={selectedMunicipio}
                          >
                            {municipios.map((muni) => (
                              <Select.Option key={muni} value={muni}>
                                {muni}
                              </Select.Option>
                            ))}
                          </Select>
                        </div>
                      </Col>

                      {/*Filtro por tipo de novedad */}
                      <Col xs={24} sm={12} md={6} lg={4}>
                        <div className="filter-item">
                          <Title
                            level={5}
                            className="filter-label"
                            style={{ color: "#042956" }}
                          >
                            Tipo de novedad
                          </Title>
                          <Select
                            style={{ width: "100%" }}
                            showSearch
                            value={selectedTipoNovedad}
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                              option.children
                                .toLowerCase()
                                .includes(input.toLowerCase())
                            }
                            onChange={handletTipoNovedad}
                          >
                            {listatipoNovedades.map((tipo) => (
                              <Option key={tipo.id} value={tipo.id}>
                                {tipo.tipo_novedad}
                              </Option>
                            ))}
                          </Select>
                        </div>
                      </Col>

                      {/* Barra de búsqueda */}
                      <Col xs={24} sm={12} md={6} lg={4}>
                        <div className="filter-item">
                          <Title
                            level={5}
                            className="filter-label"
                            style={{ color: "#042956" }}
                          >
                            Búsqueda
                          </Title>
                          <Input
                            placeholder="Buscar..."
                            prefix={<SearchOutlined />}
                            value={searchText}
                            onChange={handleSearch}
                          />
                        </div>
                      </Col>

                      {/* Botón Limpiar */}
                      <Col xs={12} sm={6} md={3} lg={1}>
                        <Button
                          type="primary"
                          icon={<ClearOutlined />}
                          style={{ width: "100%", marginTop: "24px" }}
                          onClick={handleClearFilters}
                          disabled={
                            !(
                              selectedDepartamento ||
                              searchText ||
                              selectedMunicipio ||
                              selectedTipoNovedad
                            )
                          }
                        ></Button>
                      </Col>

                      {/* Botón Exportar data */}
                      {permissionExport && (
                        <Col
                          xs={12}
                          sm={6}
                          md={3}
                          lg={2}
                          style={{
                            width: "100%",
                            marginTop: "24px",
                            borderColor: "#52c41a",
                          }}
                        >
                          <ExportNovedades
                            dataCompleta={filteredData}
                          ></ExportNovedades>
                        </Col>
                      )}
                      {/* Botón Crear Novedad */}
                      {permissionInsert && (
                        <Col xs={12} sm={6} md={3} lg={2}>
                          <Button
                            type="primary"
                            style={{
                              width: "100%",
                              marginTop: "24px",
                              backgroundColor: "#52c41a",
                              borderColor: "#52c41a",
                            }}
                            icon={<PlusOutlined />}
                            onClick={() => {
                              setInitialData({}); // Restablece initialData para crear una nueva novedad
                              setShowFormModal(true); // Abre el modal
                              setIsEditMode(false)
                            }}
                          >
                            Nuevo
                          </Button>
                        </Col>
                      )}

                      {/* Total */}
                      <Col xs={12} sm={6} md={3} lg={3}>
                        <Card
                          size="small"
                          style={{
                            marginTop: "24px",
                            textAlign: "center",
                            backgroundColor: "#f0f5ff", // Fondo azul claro
                          }}
                        >
                          <Statistic
                            title="Total Registros"
                            value={filteredData.length}
                            valueStyle={{
                              color: "#1890ff",
                              fontWeight: "bold",
                            }}
                          />
                        </Card>
                      </Col>
                    </Row>
                  </div>
                </Col>
              </Row>

              <div className="row mb-4">
                <div className="col-12">
                  {filteredData.length > 0 ? (
                    <Table
                      columns={columns}
                      dataSource={filteredData}
                      rowKey="id"
                      bordered
                      scroll={{ y: 500, x: "max-content" }}
                      pagination={{
                        current: currentPage, // página actual
                        pageSize: pageSize, // filas por página
                        total: filteredData.length, // total de registros
                        showSizeChanger: true,
                        pageSizeOptions: ["10", "20", "50", "100"],
                        showTotal: (total, range) =>
                          `${range[0]}-${range[1]} de ${total} registros`,
                        onChange: (page, size) => {
                          setCurrentPage(page);
                          setPageSize(size);
                        },
                      }}
                    />
                  ) : (
                    <div style={{ textAlign: "center", margin: "20px" }}>
                      <Alert
                        message="No hay resultados"
                        description="No se encontraron novedades que coincidan con tu búsqueda."
                        type="info"
                        showIcon
                      />
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* Modal para el formulario de novedades */}
      <Modal
        open={showFormModal}
        onCancel={() => {
          handleCancel();
        }} // Cierra el modal
        footer={null} // Sin pie de modal
        width={1200} // Ajusta el ancho del modal aquí
      >
        <FormularioNovedades
          initialData={initialData}
          isEditMode={isEditMode}
          onClose={() => {
            setShowFormModal(false);
            setIsEditMode(false);
          }} // Pasar la función para cerrar el modal
          fetchData={fetchNovedades}
        />
      </Modal>
    </>
  );
};

export default TablaNovedades;